import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Calendar,
  Check,
  ChevronDown,
  ClipboardCheck,
  Clock,
  FileText,
  Image as ImageIcon,
  Info,
  Layers,
  ShieldCheck,
  Trash2
} from 'lucide-react';
import { fireAnalytics } from '../../../utils/firebase';
import { getSessionId, getDeviceCategory } from '../../../utils/analyticsHelpers.ts';
import styles from './NewProjectFormV2.module.scss';
import { 
  NewProjectFormData, 
  NewProjectImage, 
  NewProjectValidationState 
} from '../../../types/newProject';
import { newProjectServiceUtils } from '../../../services/newProjectService.ts';
import PlacesAutocomplete from '../PlacesAutocomplete/PlacesAutocomplete.tsx';
import { collectUniquePrecargaManualFiles } from '../../../utils/conversationalMode/projectAutofillDraftUtils.ts';
import { useTrustSnapshot } from '../../../hooks/useTrustSnapshot.ts';
import { usePhoneViewport } from '../../../hooks/usePhoneViewport.ts';
import PublicTrustSnapshotCard from '../PublicTrust/PublicTrustSnapshotCard.tsx';

interface NewProjectFormV2Props {
  projectData: NewProjectFormData;
  projectImages: NewProjectImage[];
  validation: NewProjectValidationState;
  onProjectDataChange: (data: Partial<NewProjectFormData>) => void;
  onImagesChange: (images: NewProjectImage[]) => void;
  isEditing?: boolean;
  isLoading?: boolean;
  showWelcome?: boolean;
  trustJurisdictionId?: number;
  onStartManualFlow?: () => void;
  onChooseAssistedPrecarga?: (opts?: { files?: File[] }) => void;
}

const NewProjectFormV2: React.FC<NewProjectFormV2Props> = ({
  projectData,
  projectImages,
  validation,
  onProjectDataChange,
  onImagesChange,
  isEditing = false,
  isLoading = false,
  showWelcome = false,
  trustJurisdictionId,
  onStartManualFlow,
  onChooseAssistedPrecarga
}) => {
  const manualWelcomeSteps = [
    'Definís nombre, tipo y ventana de cotización.',
    'Agregás los rubros de tu obra.',
    'Subís documentación en cada rubro.',
    'Revisás todo y publicás cuando estés listo.',
  ];

  const welcomeFeatureHighlights = [
    { icon: Layers, label: 'Publicás rubros y ventanas de cotización' },
    { icon: ClipboardCheck, label: 'Revisás todo antes de publicar' },
    { icon: ShieldCheck, label: 'Vos aprobás cada acceso' },
  ];

  const [localValidation, setLocalValidation] = useState<NewProjectValidationState>(validation);
  const [localImages, setLocalImages] = useState<NewProjectImage[]>(projectImages);
  const [isDragOver, setIsDragOver] = useState(false);
  const [welcomePdfDragOver, setWelcomePdfDragOver] = useState(false);
  const [welcomePdfDropError, setWelcomePdfDropError] = useState<string | null>(null);
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);
  const [manualStepsExpanded, setManualStepsExpanded] = useState(false);
  const isPhoneViewport = usePhoneViewport();
  const manualWelcomeStepsList = (
    <>
      {manualWelcomeSteps.map((step, index) => (
        <li key={step} className={styles.welcomeNumberedStep}>
          <span className={styles.welcomeNumberedStepIndex} aria-hidden>
            {index + 1}
          </span>
          <span>{step}</span>
        </li>
      ))}
    </>
  );

  const { snapshot: trustSnapshot, isLoading: trustLoading } = useTrustSnapshot({
      enabled: showWelcome,
      jurisdictionId: trustJurisdictionId,
      limit: 4,
      context: 'private_project_publish',
      analyticsSource: 'obra_privada_welcome',
    });

  useEffect(() => {
    setLocalValidation(validation);
  }, [validation]);

  useEffect(() => {
    setLocalImages(projectImages);
  }, [projectImages]);

  const Tooltip = ({ content, children }: { content: string; children: React.ReactNode }) => (
    <div className={styles.tooltipContainer}>
      {children}
      <div className={styles.tooltipTriggerWrapper}>
        <button type="button" className={styles.tooltipTrigger} aria-label="Más información">
          <Info size={14} />
        </button>
        <div className={styles.tooltip} role="tooltip">
          {content}
        </div>
      </div>
    </div>
  );

  const formatDateForInput = useCallback((dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  }, []);

  // Obtener fecha mínima (hoy)
  const getMinDate = useCallback((): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // Manejar cambios en inputs
  const handleInputChange = useCallback((field: keyof NewProjectFormData, value: string) => {
    onProjectDataChange({ [field]: value });
    
    // Limpiar error específico cuando el usuario empieza a escribir
    if (localValidation.project[field]) {
      setLocalValidation(prev => ({
        ...prev,
        project: { ...prev.project, [field]: undefined }
      }));
    }
  }, [onProjectDataChange, localValidation.project]);

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    setFileUploadError(null);
    const newImages: NewProjectImage[] = [];
    const errors: string[] = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const sizeOk = newProjectServiceUtils.validateFileSize(file, 10 * 1024 * 1024);
      const typeOk = newProjectServiceUtils.validateFileType(file, ['image/jpeg', 'image/png', 'image/webp']);
      
      if (!sizeOk) {
        errors.push('El archivo es demasiado grande (máximo 10MB)');
        continue;
      }
      if (!typeOk) {
        errors.push('Tipo de archivo no permitido. Usá JPG, PNG o WebP.');
        continue;
      }
      
      const preview = await newProjectServiceUtils.createImagePreview(file);
      const newImage: NewProjectImage = {
        id: `temp_${Date.now()}_${i}_${Math.random().toString(36).substr(2, 9)}`,
        file,
        preview,
        isNew: true
      };
      newImages.push(newImage);
    }
    
    if (errors.length > 0) {
      const message = errors.length === 1
        ? errors[0]
        : `Algunos archivos no se pudieron agregar. Máximo 10MB, formatos: JPG, PNG o WebP.`;
      setFileUploadError(message);
    }
    
    if (newImages.length > 0) {
      const updatedImages = [...localImages, ...newImages];
      setLocalImages(updatedImages);
      onImagesChange(updatedImages);
      if (errors.length === 0) setFileUploadError(null);
    }
    
    event.target.value = '';
  }, [localImages, onImagesChange]);

  // Eliminar imagen
  const handleRemoveImage = useCallback((imageId: string) => {
    const updatedImages = localImages.filter(img => img.id !== imageId);
    
    // Actualizar estado local primero
    setLocalImages(updatedImages);
    
    // Luego notificar al padre
    onImagesChange(updatedImages);
  }, [localImages, onImagesChange]);

  // Drag & Drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      // Simular el evento de input file
      const mockEvent = {
        target: { files, value: '' }
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      
      handleFileSelect(mockEvent);
    }
  }, [handleFileSelect]);

  const handleStartManualFlow = useCallback(() => {
    fireAnalytics("new_project_empty_state_cta_click", {
      category: "private_projects",
      action: "empty_state_begin",
      session_id: getSessionId(),
      device_category: getDeviceCategory(),
    });
    onStartManualFlow?.();
  }, [onStartManualFlow]);

  const invokeAssistedPrecargaFromWelcome = useCallback(() => {
    if (!onChooseAssistedPrecarga) return;
    setWelcomePdfDropError(null);
    fireAnalytics("new_project_empty_state_cta_click", {
      category: "private_projects",
      action: "empty_state_assisted_precarga",
      session_id: getSessionId(),
      device_category: getDeviceCategory(),
    });
    onChooseAssistedPrecarga();
  }, [onChooseAssistedPrecarga]);

  const handleWelcomePdfDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setWelcomePdfDragOver(true);
  }, []);

  const handleWelcomePdfDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setWelcomePdfDragOver(false);
  }, []);

  const handleWelcomePdfDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setWelcomePdfDragOver(false);
      if (!onChooseAssistedPrecarga) return;
      const list = collectUniquePrecargaManualFiles(Array.from(e.dataTransfer.files || []));
      if (list.length === 0) {
        setWelcomePdfDropError('Solo se admiten PDF, imágenes o Excel (.xls, .xlsx).');
        return;
      }
      setWelcomePdfDropError(null);
      onChooseAssistedPrecarga({ files: list });
    },
    [onChooseAssistedPrecarga]
  );

  // Validar fechas en tiempo real
  const validateDates = useCallback(() => {
    if (projectData.startDate && projectData.endDate) {
      const isValid = newProjectServiceUtils.validateDateRange(projectData.startDate, projectData.endDate);
      if (!isValid) {
        setLocalValidation(prev => ({
          ...prev,
          project: { ...prev.project, endDate: 'La fecha de finalización debe ser posterior a la fecha de inicio' }
        }));
      }
    }
  }, [projectData.startDate, projectData.endDate]);

  // Efecto para validar fechas
  useEffect(() => {
    validateDates();
  }, [validateDates]);

  return (
    <div className={styles.formContainer}>
      <motion.div
        className={showWelcome ? styles.welcomeShell : styles.form}
        initial={{ opacity: 0, y: showWelcome ? 16 : 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: showWelcome ? 0.45 : 0.6 }}
      >
        {showWelcome ? (
          <div className={styles.welcomeBlock}>
            <div className={styles.welcomeLayout}>
              <div className={styles.welcomeContent}>
                <div className={styles.welcomeIntro}>
                  <h2 className={styles.welcomeTitle}>Publicá tu obra privada y recibí presupuestos</h2>
                  <p className={styles.welcomeText}>
                    Agregá rubros, definí documentación y ventanas de cotización.
                  </p>
                </div>
                <PublicTrustSnapshotCard snapshot={trustSnapshot} isLoading={trustLoading} />
                {!isPhoneViewport ? (
                  <ul className={styles.welcomeHighlights}>
                    {welcomeFeatureHighlights.map(({ icon: HighlightIcon, label }) => (
                      <li key={label} className={styles.welcomeHighlightItem}>
                        <HighlightIcon
                          size={18}
                          strokeWidth={2}
                          className={styles.welcomeHighlightIcon}
                          aria-hidden
                        />
                        <span className={styles.welcomeHighlightLabel}>{label}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
                <div className={styles.welcomeCards}>
                  {onChooseAssistedPrecarga ? (
                    <article
                      className={`${styles.welcomeChoiceCard} ${styles.welcomeChoiceCardRecommended}`}
                      aria-labelledby="new-project-welcome-assisted-title"
                    >
                      <div className={styles.welcomeChoiceCardHeader}>
                        <span className={styles.welcomeIaBadge}>IA · Recomendado</span>
                      </div>
                      <h3 id="new-project-welcome-assisted-title" className={styles.welcomeChoiceCardTitle}>
                        Desde documentación
                      </h3>
                      <p className={styles.welcomeChoiceCardDescription}>
                        Subí tu documentación (PDF, imágenes o Excel) y la IA detecta rubros, ordena documentación y arma un
                        borrador para que revises antes de publicar.
                      </p>
                      <div
                        className={`${styles.welcomeAgentDrop} ${welcomePdfDragOver ? styles.welcomeAgentDropActive : ''}`}
                        onDragOver={handleWelcomePdfDragOver}
                        onDragLeave={handleWelcomePdfDragLeave}
                        onDrop={handleWelcomePdfDrop}
                      >
                        <FileText size={28} strokeWidth={1.75} className={styles.welcomeAgentDropIcon} aria-hidden />
                        <span className={styles.welcomeAgentDropHint}>Soltá tus archivos acá o</span>
                        <button
                          type="button"
                          className={styles.welcomeButtonOutline}
                          onClick={() => invokeAssistedPrecargaFromWelcome()}
                          disabled={isLoading}
                        >
                          Ir a carga con IA
                        </button>
                        <span className={styles.welcomeAgentDropTypes}>
                          Hasta 12 archivos · 20 MB c/u · PDF, imágenes o Excel
                        </span>
                        {welcomePdfDropError ? (
                          <p className={styles.welcomePdfDropError} role="alert">
                            {welcomePdfDropError}
                          </p>
                        ) : null}
                      </div>
                      <ul className={styles.welcomeCheckList}>
                        <li>
                          <Check size={16} strokeWidth={2.5} className={styles.welcomeCheckListIcon} aria-hidden />
                          <span>Detectamos rubros y documentación de tu archivo</span>
                        </li>
                        <li>
                          <Check size={16} strokeWidth={2.5} className={styles.welcomeCheckListIcon} aria-hidden />
                          <span>Armamos un borrador para que revises y ajustes</span>
                        </li>
                        <li>
                          <Check size={16} strokeWidth={2.5} className={styles.welcomeCheckListIcon} aria-hidden />
                          <span>Publicás cuando esté listo</span>
                        </li>
                      </ul>
                      <p className={styles.welcomeEstimatedTime}>
                        <Clock size={15} strokeWidth={2} aria-hidden />
                        <span>Tiempo estimado: 5–10 minutos</span>
                      </p>
                    </article>
                  ) : null}
                  <article
                    className={`${styles.welcomeChoiceCard} ${styles.welcomeChoiceCardManual} ${isPhoneViewport ? styles.welcomeChoiceCardManualCompact : ''}`}
                    aria-labelledby="new-project-welcome-manual-title"
                  >
                    <div className={styles.welcomeChoiceCardHeader}>
                      <span className={styles.welcomeManualBadge}>Manual</span>
                      {isPhoneViewport ? (
                        <button
                          type="button"
                          className={styles.welcomeManualStepsToggle}
                          onClick={() => setManualStepsExpanded((expanded) => !expanded)}
                          aria-expanded={manualStepsExpanded}
                          aria-controls="new-project-welcome-manual-steps"
                        >
                          <span>{manualStepsExpanded ? 'Ocultar pasos' : 'Ver pasos'}</span>
                          <ChevronDown
                            size={16}
                            strokeWidth={2}
                            className={`${styles.welcomeManualStepsToggleIcon} ${manualStepsExpanded ? styles.welcomeManualStepsToggleIconExpanded : ''}`}
                            aria-hidden
                          />
                        </button>
                      ) : null}
                    </div>
                    <h3 id="new-project-welcome-manual-title" className={styles.welcomeChoiceCardTitle}>
                      Carga manual
                    </h3>
                    <p className={styles.welcomeChoiceCardDescription}>
                      {isPhoneViewport
                        ? 'Campo por campo, a tu ritmo y con control total.'
                        : 'Completá proyecto, rubros y documentación campo por campo, a tu ritmo y con control total.'}
                    </p>
                    {isPhoneViewport ? (
                      <div
                        id="new-project-welcome-manual-steps"
                        className={`${styles.welcomeManualStepsPanel} ${manualStepsExpanded ? styles.welcomeManualStepsPanelExpanded : ''}`}
                        aria-hidden={!manualStepsExpanded}
                      >
                        <div className={styles.welcomeManualStepsPanelInner}>
                          <ol className={styles.welcomeNumberedSteps}>{manualWelcomeStepsList}</ol>
                        </div>
                      </div>
                    ) : (
                      <ol id="new-project-welcome-manual-steps" className={styles.welcomeNumberedSteps}>
                        {manualWelcomeStepsList}
                      </ol>
                    )}
                    {!isPhoneViewport ? (
                      <p className={styles.welcomeCardSocialNote}>
                        Tu obra va a ser visible para proveedores de cada rubro que agregues.
                      </p>
                    ) : null}
                    <button
                      type="button"
                      className={styles.welcomeButtonOutline}
                      onClick={handleStartManualFlow}
                      disabled={isLoading}
                    >
                      Empezar manualmente
                    </button>
                    <p className={styles.welcomeEstimatedTime}>
                      <Clock size={15} strokeWidth={2} aria-hidden />
                      <span>Tiempo estimado: 15–25 minutos</span>
                    </p>
                  </article>
                </div>
                {isPhoneViewport ? (
                  <ul className={styles.welcomeHighlights}>
                    {welcomeFeatureHighlights.map(({ icon: HighlightIcon, label }) => (
                      <li key={label} className={styles.welcomeHighlightItem}>
                        <HighlightIcon
                          size={18}
                          strokeWidth={2}
                          className={styles.welcomeHighlightIcon}
                          aria-hidden
                        />
                        <span className={styles.welcomeHighlightLabel}>{label}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.formHeader}>
              <h2 className={styles.formTitle}>Datos del proyecto</h2>
              <p className={styles.formSubtitle}>
                {isEditing
                  ? 'Modificá los datos de tu proyecto'
                  : 'Completá la publicación del proyecto en Edify; después agregá rubros y definí en cada uno su ventana de cotización.'
                }
              </p>
            </div>
            <div className={styles.formBody}>
              <div className={styles.formSection}>
                <div className={styles.formFields}>
                  <div className={styles.nameLocationFields}>
            <div className={styles.fieldGroup}>
              <label className={styles.label} htmlFor="projectName">
                <FileText size={18} />
                Nombre del Proyecto *
              </label>
              <input
                id="projectName"
                type="text"
                className={`${styles.input} ${localValidation.project.name ? styles.inputError : ''}`}
                value={projectData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Ej: Construcción de casa familiar"
                disabled={isLoading}
              />
              {localValidation.project.name && (
                <span className={styles.errorText}>{localValidation.project.name}</span>
              )}
            </div>

            <div className={styles.fieldGroup}>
              <PlacesAutocomplete
                value={projectData.zone}
                onChange={(value) => handleInputChange('zone', value)}
                placeholder="Ej: Palermo, CABA"
                error={localValidation.project.zone}
                disabled={isLoading}
              />
            </div>
                  </div>
                </div>
              </div>
              <div className={styles.formSection}>
                <div className={styles.formFields}>
                  <div className={styles.dateFields}>
            <div className={styles.fieldGroup}>
              <Tooltip content="Fecha en que tu proyecto comienza a publicarse en Edify. Durante este período podés agregar rubros para recibir cotizaciones; cada rubro tiene sus propias fechas de presupuesto. No es el inicio de la obra; esta fecha es editable y podés extenderla.">
                <label className={styles.label} htmlFor="startDate">
                  <Calendar size={18} />
                  Fecha de Inicio *
                </label>
              </Tooltip>
              <input
                id="startDate"
                type="date"
                className={`${styles.input} ${localValidation.project.startDate ? styles.inputError : ''}`}
                value={formatDateForInput(projectData.startDate)}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                min={getMinDate()}
                disabled={isLoading}
              />
              {localValidation.project.startDate && (
                <span className={styles.errorText}>{localValidation.project.startDate}</span>
              )}
            </div>

            <div className={styles.fieldGroup}>
              <Tooltip content="Fecha hasta la cual tu proyecto permanece publicado en Edify. Hasta entonces podés seguir agregando rubros para recibir cotizaciones; cada rubro tiene sus propias fechas de presupuesto. No es el fin de la obra; podés editarla y extenderla cuando lo necesites.">
                <label className={styles.label} htmlFor="endDate">
                  <Calendar size={18} />
                  Fecha de Finalización *
                </label>
              </Tooltip>
              <input
                id="endDate"
                type="date"
                className={`${styles.input} ${localValidation.project.endDate ? styles.inputError : ''}`}
                value={formatDateForInput(projectData.endDate)}
                onChange={(e) => handleInputChange('endDate', e.target.value)}
                min={formatDateForInput(projectData.startDate) || getMinDate()}
                disabled={isLoading}
              />
              {localValidation.project.endDate && (
                <span className={styles.errorText}>{localValidation.project.endDate}</span>
              )}
            </div>
                  </div>
                </div>
              </div>
              <div className={styles.formSection}>
                <div className={styles.formFields}>
                  <div className={styles.fieldGroup}>
            <label className={styles.label} htmlFor="projectDescription">
              <FileText size={18} />
              Descripción del Proyecto *
            </label>
            <textarea
              id="projectDescription"
              className={`${styles.textarea} ${localValidation.project.description ? styles.inputError : ''}`}
              value={projectData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe detalladamente tu proyecto, incluyendo características especiales, materiales preferidos, etc."
              rows={4}
              disabled={isLoading}
            />
            {localValidation.project.description && (
              <span className={styles.errorText}>{localValidation.project.description}</span>
            )}
                  </div>
                </div>
              </div>
              <div className={styles.formSection}>
                <div className={styles.formFields}>
                  <div className={styles.fieldGroup}>
            <div className={styles.imageHeader}>
              <div className={styles.imageHeaderContent}>
                <label className={styles.label}>
                  <ImageIcon size={18} />
                  Imágenes del Proyecto *
                </label>
                <p className={styles.imageSubtitle}>
                  Comparte imagenes representativas de tu proyecto
                </p>
              </div>
              
              <button 
                className={styles.addButton}
                onClick={() => document.getElementById('imageUpload')?.click()}
                disabled={isLoading}
              >
                <ImageIcon size={20} />
                Agregar imagen 
              </button>
            </div>
            
            {/* Input de archivos oculto */}
            <input
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileSelect}
              className={styles.hiddenFileInput}
              id="imageUpload"
              disabled={isLoading}
            />
            
            {/* Estado vacío - Solo emptyState */}
            {localImages.length === 0 && (
              <div 
                className={`${styles.emptyState} ${isDragOver ? styles.dragOver : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <ImageIcon size={48} className={styles.emptyStateIcon} />
                <h3 className={styles.emptyStateTitle}>No hay imágenes cargadas</h3>
                <p className={styles.emptyStateText}>
                  Usa el botón "Agregar imagen" para cargar fotos de tu proyecto
                </p>
                <p className={styles.hintText}>
                  También puedes arrastrar archivos aquí
                </p>
              </div>
            )}

            {/* Estado con imágenes - Grid + botón de agregar */}
            {localImages.length > 0 && (
              <div className={styles.imageSection}>
                <div className={styles.imageList}>
                  {localImages.map((image) => {
                    return (
                    <div key={image.id} className={styles.imageItem}>
                      <img 
                        src={image.preview} 
                        alt="Preview" 
                        className={styles.imagePreview}
                      />
                      <button
                        type="button"
                        className={styles.removeImageButton}
                        onClick={() => handleRemoveImage(image.id!)}
                        disabled={isLoading}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    );
                  })}
                  
                  {/* Botón para agregar más imágenes */}
                  <div 
                    className={`${styles.addMoreButton} ${isDragOver ? styles.dragOver : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById('imageUpload')?.click()}
                  >
                    <div className={styles.addMoreContent}>
                      <ImageIcon size={24} />
                      <span>Agregar más</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {fileUploadError && (
              <span className={styles.errorText}>{fileUploadError}</span>
            )}
            {localValidation.project.image && (
              <span className={styles.errorText}>{localValidation.project.image}</span>
            )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default NewProjectFormV2;
